import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";

const HomePage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const navigate = useNavigate();

  const cards = [
    {
      id: 'language',
      title: 'Language Learning',
      path: '/languages',
      icon: '🌎',
      description: 'Master vocabulary and grammar in multiple languages',
      color: '#6366f1'
    },
    {
      id: 'decks',
      title: 'Custom Decks',
      path: '/decks',
      icon: '🃏',
      description: 'Create and study your own custom flashcard decks',
      color: '#10b981'
    },
    {
      id: 'trivia',
      title: 'Trivia',
      path: '/trivia',
      icon: '🧠',
      description: 'Challenge yourself with fun trivia across various topics',
      color: '#f43f5e'
    }
  ];

  const handleCardClick = (path) => {
    console.log(`Navigating to ${path}`);
    // In a real app, you'd use React Router here:
    navigate(path);
  };

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: '#f1f5f9',
    color: '#0f172a',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  };

  const headerStyle = {
    background: 'linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(168, 85, 247, 0.1))',
    paddingTop: '4rem',
    paddingBottom: '3rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    textAlign: 'center'
  };

  const headerContainerStyle = {
    maxWidth: '64rem',
    margin: '0 auto'
  };

  const titleStyle = {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    fontFamily: 'Helvetica',

    background: 'linear-gradient(to right, #2563eb, #9333ea)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '1rem'
  };

  const subtitleStyle = {
    fontSize: '1.25rem',
    fontFamily: 'Helvetica',
    lineHeight: '1.5',
    color: '#334155',
    maxWidth: '36rem',
    margin: '0 auto'
  };

  const mainStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '3rem 1rem'
  };

  const promptStyle = {
    fontSize: '1.5rem',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: '2.5rem'
  };

  const cardsContainerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1.5rem'
  };

  const getCardStyle = (hoveredId, cardId, cardColor) => ({
    cursor: 'pointer',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    backgroundColor: 'white',
    boxShadow: hoveredId === cardId ?
      '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' :
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    border: '1px solid #e2e8f0',
    height: '100%',
    transform: hoveredId === cardId ? 'scale(1.03)' : 'scale(1)',
    transition: 'all 0.3s ease'
  });

  const cardBorderStyle = (color) => ({
    height: '8px',
    background: `linear-gradient(to right, ${color}, ${adjustColor(color, -20)})`
  });

  // Helper function to darken a color
  function adjustColor(color, amount) {
    return color + (amount < 0 ? '99' : '33');
  }

  const cardContentStyle = {
    padding: '1.5rem'
  };

  const cardHeaderStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem'
  };

  const iconStyle = {
    fontSize: '2rem',
    marginBottom: '0.5rem'
  };

  const getArrowStyle = (hoveredId, cardId, cardColor) => ({
    width: '2.5rem',
    height: '2.5rem',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(to right, ${cardColor}, ${adjustColor(cardColor, -20)})`,
    color: 'white',
    opacity: hoveredId === cardId ? 1 : 0,
    transition: 'opacity 0.3s ease'
  });

  const cardTitleStyle = {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    marginBottom: '0.5rem'
  };

  const cardDescriptionStyle = {
    color: '#64748b'
  };

  const footerStyle = {
    maxWidth: '72rem',
    margin: '0 auto',
    padding: '2rem 1rem',
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.875rem'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <header style={headerStyle}>
        <div style={headerContainerStyle}>
          <h1 style={titleStyle}>ankiverse.com</h1>
          <p style={subtitleStyle}>
            Enhance your learning through the science of spaced repetition.
            Remember more with less effort and make knowledge stick forever.
          </p>
        </div>
      </header>

      {/* Main content */}
      <main style={mainStyle}>
        <h2 style={promptStyle}>What would you like to study today?</h2>

        <div style={cardsContainerStyle}>
          {cards.map((card) => (
            <div
              key={card.id}
              style={getCardStyle(hoveredCard, card.id, card.color)}
              onClick={() => handleCardClick(card.path)}
              onMouseEnter={() => setHoveredCard(card.id)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={cardBorderStyle(card.color)}></div>
              <div style={cardContentStyle}>
                <div style={cardHeaderStyle}>
                  <div style={iconStyle}>{card.icon}</div>
                  <div style={getArrowStyle(hoveredCard, card.id, card.color)}>
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </div>
                <h3 style={cardTitleStyle}>{card.title}</h3>
                <p style={cardDescriptionStyle}>{card.description}</p>
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer style={footerStyle}>
        <p>© 2025 ankiverse.com • Powered by science-based learning</p>
      </footer>
    </div>
  );
};

export default HomePage;